import { BreadcrumbItem } from "@episerver/ui-framework";
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { EnumData } from "../../EnumManager/model/Enum";
import { fetchEnumsByType } from "../../EnumManager/reducers/asyncThunks";
import { enumType } from "../../EnumManager/reducers/enumManagerReducer";
import { Organization, OrganizationTab } from "../model/organization";
import { fetchOrganizationById, fetchOrganizations, updateOrganization } from "./asyncThunks";
import { Address } from "../../AddressManager/model/address";
import { Contact } from "../../ContactManager/model/contact";
import { Property } from "../../../businessFoundation/MetaClassData/MetaClassDataList/MetaClassDataModel";

export const organizationTitle = { Add: "Create Organization", Edit: "Edit Organization" };

export const initialOrganizationDetailState = {
    title: "",
    organization: {
        primaryKeyId: null,
        name: "",
        description: "",
        organizationTypeId: "0",
        organizationType: "",
        organizationCustomerGroupId: "0",
        organizationCustomerGroup: "",
        businessCategoryId: "0",
        businessCategory: "",
        parentId: null,
        extendedProperties: [] as Property[],
        contacts: [] as Contact[],
        children: [] as Organization[],
        addresses: [] as Address[],
    } as Organization,
    organizationId: "",
    organizationTypes: [] as EnumData[],
    organizationCustomerGroups: [] as EnumData[],
    businessCategories: [] as EnumData[],
    breadCrumbItems: [
        {
            title: "Organizations",
            level: 1,
            link: "#/Organizations",
        },
    ] as BreadcrumbItem[],
    extendedPropertyErrors: [] as Error[],
    activeTab: 0,
    error: null,
};

export const organizationDetailSlice = createSlice({
    name: "organizationDetail",
    initialState: initialOrganizationDetailState,
    reducers: {
        changeTitle: (state, action): void => {
            state.title = action.payload;
        },
        setParentId: (state, action): void => {
            state.organization.parentId = action.payload;
        },
        onChangeProperty: (state, action): void => {
            state.organization = action.payload;
        },
        changeExtendedPropertyErrors: (state, action): void => {
            let { name, message } = action.payload;
            let error = state.extendedPropertyErrors.find((error: Error) => error.name === name);
            if (error) {
                state.extendedPropertyErrors = state.extendedPropertyErrors
                    .filter((error: Error) => {
                        if (error.name === name && message === "") return false;
                        return true;
                    })
                    .map((error: Error) => {
                        if (error.name === name) return { ...error, message: message };
                        return error;
                    });
            } else {
                if (message) {
                    state.extendedPropertyErrors.push({ name: name, message: message });
                }
            }
        },
        updateBreadcrumb: (state, action: { payload: BreadcrumbItem }): void => {
            state.breadCrumbItems = [
                ...state.breadCrumbItems.filter((item: BreadcrumbItem) => item.level !== action.payload.level),
                action.payload,
            ];
        },
        updateOrganizationId: (state, action): void => {
            state.organizationId = action.payload;
        },
        changeActiveTab: (state, action): void => {
            state.activeTab = action.payload;
        },
        reset: (state): void => {
            state.organization = initialOrganizationDetailState.organization;
        },
        onAddOrUpdateOrganizationAddress: (state, action): void => {
            const { idx, isNew, ...address } = action.payload;
            if (isNew) {
                state.organization.addresses.push(address);
            } else {
                state.organization.addresses = state.organization.addresses.map((t, index) => {
                    if (index == idx) return address;
                    return t;
                });
            }
        },
        onDeleteOrganizationAddress: (state, action): void => {
            let addresses = [...state.organization.addresses];
            state.organization.addresses = addresses.filter((v) => action.payload.primaryKeyId != v.primaryKeyId);
        },
        onAddOrUpdateContact: (state, action): void => {
            action.payload.contacts.forEach((e: Contact) => {
                let isNew = state.organization.contacts.find((x) => x.primaryKeyId == e.primaryKeyId) == null;
                if (isNew) {
                    e = { ...e, ownerId: action.payload.organizationId };
                    state.organization.contacts.push(e);
                }
            });
        },
        onAddOrUpdateChildren: (state, action): void => {
            let newChildArray = [] as Organization[];
            Object.assign(newChildArray, state.organization.children);
            action.payload.children.forEach((e: Organization) => {
                let isNew = state.organization.children.find((x) => x.primaryKeyId == e.primaryKeyId) == null;
                if (isNew) {
                    e = { ...e, parentId: action.payload.parentOrganizationId };
                    newChildArray.push(e);
                }
            });
            state.organization.children = newChildArray;
        },
    },
    extraReducers: {
        [`${fetchOrganizationById.fulfilled}`]: (state, action): void => {
            state.organization = action.payload;
        },
        [`${fetchEnumsByType.fulfilled}`]: (state, action): void => {
            if (action.payload.type === enumType.OrganizationType)
                state.organizationTypes = [
                    ...initialOrganizationDetailState.organizationTypes,
                    ...action.payload.enums,
                ];
            else if (action.payload.type === enumType.ContactGroup)
                state.organizationCustomerGroups = [
                    ...initialOrganizationDetailState.organizationCustomerGroups,
                    ...action.payload.enums,
                ];
            else
                state.businessCategories = [
                    ...initialOrganizationDetailState.businessCategories,
                    ...action.payload.enums,
                ];
        },
        [`${updateOrganization.rejected}`]: (state, action): void => {
            state.error = action.payload;
        },
    },
});

// Actions
export const {
    changeTitle,
    setParentId,
    onChangeProperty,
    changeExtendedPropertyErrors,
    updateBreadcrumb,
    changeActiveTab,
    updateOrganizationId,
    reset,
    onAddOrUpdateChildren,
    onAddOrUpdateOrganizationAddress,
    onDeleteOrganizationAddress,
    onAddOrUpdateContact,
} = organizationDetailSlice.actions;

// Selectors
export const titleSelector = createSelector(
    (state: RootState): string => state.organizationDetail.title,
    (title: string): string => title
);

export const organizationSelector = createSelector(
    (state: RootState): Organization => state.organizationDetail.organization,
    (organization: Organization): Organization => organization
);

export const organizationTypesSelector = createSelector(
    (state: RootState): EnumData[] => state.organizationDetail.organizationTypes,
    (organizationTypes: EnumData[]): EnumData[] => organizationTypes
);

export const organizationCustomerGroupsSelector = createSelector(
    (state: RootState): EnumData[] => state.organizationDetail.organizationCustomerGroups,
    (customerGroups: EnumData[]): EnumData[] => customerGroups
);

export const businessCategoriesSelector = createSelector(
    (state: RootState): EnumData[] => state.organizationDetail.businessCategories,
    (businessCategories: EnumData[]): EnumData[] => businessCategories
);

export const breadcrumbItemsSelector = createSelector(
    (state: RootState): BreadcrumbItem[] => state.organizationDetail.breadCrumbItems,
    (breadCrumbItems: BreadcrumbItem[]): BreadcrumbItem[] => breadCrumbItems
);

export const extendedPropertyErrorsSelector = createSelector(
    (state: RootState): Error[] => state.organizationDetail.extendedPropertyErrors,
    (extendedPropertyErrors: Error[]): Error[] => extendedPropertyErrors
);

export const activeTabSelector = createSelector(
    (state: RootState): OrganizationTab => state.organizationDetail.activeTab,
    (activeTab: OrganizationTab): OrganizationTab => activeTab
);

export const errorSelector = createSelector(
    (state: RootState): any => state.organizationDetail.error,
    (error: any) => error
);
